import { useState } from 'react';
import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import translationFallback from '@/utilites/translations/translationFallback';
import getBasicStaticSideProps from '@/lib/staticSideProps/getBasicStaticSideProps';
import SpiritoryInput from '@/components/common/Forms/SpiritoryInput';
import SpiritoryTextarea from '@/components/common/Forms/SpiritoryTextarea';
import { PrimaryButton } from '@/components/common/PrimaryButton';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Step = 'form' | 'confirm' | 'success';

type FormState = {
  name: string;
  email: string;
  address: string;
  orderNumber: string;
  orderDate: string;
  subject: string;
  message: string;
};

const EMPTY: FormState = {
  name: '',
  email: '',
  address: '',
  orderNumber: '',
  orderDate: '',
  subject: '',
  message: '',
};

export default function WithdrawalForm() {
  const { t } = useTranslation('withdrawal');
  const router = useRouter();
  const tf = (key: string, fallback: string) => translationFallback(key, t(key), fallback);

  const [step, setStep] = useState<Step>('form');
  const [data, setData] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [confirmationSent, setConfirmationSent] = useState(true);

  const set = (field: keyof FormState) => (e: { target: { value: string } }) =>
    setData(prev => ({ ...prev, [field]: e.target.value }));

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!data.name.trim()) next.name = tf('form.error_required', 'This field is required.');
    if (!data.email.trim()) next.email = tf('form.error_required', 'This field is required.');
    else if (!EMAIL_RE.test(data.email.trim()))
      next.email = tf('form.error_email', 'Please enter a valid email address.');
    if (!data.address.trim()) next.address = tf('form.error_required', 'This field is required.');
    if (!data.subject.trim()) next.subject = tf('form.error_required', 'This field is required.');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const goToConfirm = (e: React.FormEvent) => {
    e.preventDefault();
    setGeneralError('');
    if (validate()) setStep('confirm');
  };

  const submit = async () => {
    setSubmitting(true);
    setGeneralError('');
    try {
      const res = await fetch('/api/withdrawal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, locale: (router.locale || 'en').toUpperCase() }),
      });
      const body = await res.json().catch(() => ({}));

      if (!res.ok) {
        // Field-specific errors from the backend → back to step 1.
        if (body?.errors && typeof body.errors === 'object') {
          setErrors(body.errors);
          setStep('form');
        } else {
          setGeneralError(
            tf(
              'form.error_generic',
              'Something went wrong while submitting your withdrawal. Please try again later or contact help@spiritory.com.'
            )
          );
        }
        return;
      }

      setConfirmationSent(body?.confirmationSent !== false);
      setStep('success');
    } catch {
      setGeneralError(
        tf(
          'form.error_generic',
          'Something went wrong while submitting your withdrawal. Please try again later or contact help@spiritory.com.'
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const optional = `(${tf('form.optional', 'optional')})`;

  const summaryRow = (label: string, value: string) =>
    value ? (
      <div className="tw-flex tw-flex-col tw-gap-0.5 tw-border-b tw-border-gray-100 tw-py-2 sm:tw-flex-row sm:tw-gap-4">
        <span className="tw-w-56 tw-shrink-0 tw-text-sm tw-text-gray-500">{label}</span>
        <span className="tw-whitespace-pre-line tw-text-sm tw-font-medium tw-text-[#232323]">{value}</span>
      </div>
    ) : null;

  return (
    <>
      <Head>
        <title>{tf('form.title', 'Electronic Withdrawal')} | Spiritory</title>
        <meta name="robots" content="noindex" />
      </Head>

      <div className="tw-mx-auto tw-w-full tw-max-w-[640px] tw-px-4 tw-py-10">
        <h1 className="tw-mb-2 tw-text-2xl tw-font-bold tw-text-[#232323]">
          {tf('form.title', 'Electronic Withdrawal')}
        </h1>
        <p className="tw-mb-8 tw-text-sm tw-text-gray-600">
          {tf('form.subtitle', 'Declare your withdrawal online — no login required. We will confirm receipt by email.')}
        </p>

        {generalError && (
          <div className="tw-mb-6 tw-rounded-xl tw-bg-red-50 tw-p-3 tw-text-sm tw-text-red-600">
            {generalError}
          </div>
        )}

        {step === 'form' && (
          <form onSubmit={goToConfirm} className="tw-flex tw-flex-col tw-gap-5">
            <SpiritoryInput
              required
              label={tf('form.name', 'Name')}
              value={data.name}
              onChange={set('name')}
              error={errors.name}
            />
            <SpiritoryInput
              required
              type="email"
              label={tf('form.email', 'Email')}
              value={data.email}
              onChange={set('email')}
              error={errors.email}
            />
            <SpiritoryTextarea
              required
              label={tf('form.address', 'Address')}
              rows={3}
              value={data.address}
              onChange={set('address')}
              error={errors.address}
            />
            <SpiritoryInput
              label={`${tf('form.orderNumber', 'Order number')} ${optional}`}
              value={data.orderNumber}
              onChange={set('orderNumber')}
            />
            <SpiritoryInput
              label={`${tf('form.orderDate', 'Order date')} ${optional}`}
              placeholder="10.06.2026"
              value={data.orderDate}
              onChange={set('orderDate')}
            />
            <SpiritoryTextarea
              required
              label={tf('form.subject', 'What are you withdrawing from?')}
              rows={2}
              value={data.subject}
              onChange={set('subject')}
              error={errors.subject}
            />
            <SpiritoryTextarea
              label={`${tf('form.message', 'Message')} ${optional}`}
              rows={3}
              value={data.message}
              onChange={set('message')}
            />
            <p className="tw-text-xs tw-text-gray-500">
              {tf('form.required_hint', 'Fields marked with * are required.')}{' '}
              {tf('form.no_reason_needed', 'You do not need to give a reason for your withdrawal.')}
            </p>
            <PrimaryButton type="submit">{tf('form.next', 'Continue')}</PrimaryButton>
          </form>
        )}

        {step === 'confirm' && (
          <div className="tw-flex tw-flex-col tw-gap-5">
            <p className="tw-text-sm tw-text-gray-600">
              {tf('form.review_intro', 'Please review your details. By submitting, you make a binding declaration of withdrawal.')}
            </p>
            <div className="tw-rounded-xl tw-border tw-border-gray-200 tw-p-4">
              {summaryRow(tf('form.name', 'Name'), data.name)}
              {summaryRow(tf('form.email', 'Email'), data.email)}
              {summaryRow(tf('form.address', 'Address'), data.address)}
              {summaryRow(tf('form.orderNumber', 'Order number'), data.orderNumber)}
              {summaryRow(tf('form.orderDate', 'Order date'), data.orderDate)}
              {summaryRow(tf('form.subject', 'What are you withdrawing from?'), data.subject)}
              {summaryRow(tf('form.message', 'Message'), data.message)}
            </div>
            <div className="tw-flex tw-flex-col-reverse tw-gap-3 sm:tw-flex-row sm:tw-justify-between">
              <PrimaryButton outlined type="button" disabled={submitting} onClick={() => setStep('form')}>
                {tf('form.back', 'Back')}
              </PrimaryButton>
              <PrimaryButton type="button" disabled={submitting} onClick={submit}>
                {submitting ? tf('form.submitting', 'Submitting…') : tf('form.submit', 'Submit withdrawal as binding')}
              </PrimaryButton>
            </div>
          </div>
        )}

        {step === 'success' && (
          <div className="tw-rounded-xl tw-border tw-border-gray-200 tw-p-6 tw-text-center">
            <h2 className="tw-mb-3 tw-text-xl tw-font-bold tw-text-[#232323]">
              {tf('form.success_title', 'Your withdrawal has been received')}
            </h2>
            <p className="tw-text-sm tw-text-gray-600">
              {confirmationSent
                ? tf(
                    'form.success_text',
                    'Thank you. We have received your withdrawal and sent a confirmation to your email address. Please keep it for your records. We will process your withdrawal without delay.'
                  )
                : tf(
                    'form.success_text_fallback',
                    'We have received your withdrawal. However, we could not deliver the confirmation email to the address you provided. We will get back to you shortly — you do not need to do anything further.'
                  )}
            </p>
          </div>
        )}
      </div>
    </>
  );
}

export const getStaticProps = getBasicStaticSideProps(['common', 'withdrawal']);
